import { useState } from 'react'
import { PageHead } from '@components/PageHead'
import { useFolders } from '@features/vault/hooks/useFolders'
import { VaultItemAvatar } from '@features/vault/components/VaultItemAvatar'
import { useVaultTrash } from '@features/vault/hooks/useVaultTrash'
import type { VaultItem } from '@features/vault/types'
import { formatDateJst } from '@utils/datetime'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * 保管庫のゴミ箱画面 (B5、Phase 6)。
 *
 * - `DELETE /api/vault/items/:id` で soft-delete されたアイテムを一覧表示
 * - 各行: 復元 / 完全削除 (完全削除は確認ダイアログ経由)
 * - ゴミ箱に 30 日以上あるアイテムはサーバ側で自動物理削除される
 */

const TRASH_RETENTION_DAYS = 30
const DAY_MS = 24 * 60 * 60 * 1000

/** deletedAt から自動削除までの残り日数 (切り上げ、最小 0)。 */
const daysUntilPurge = (deletedAt: string | null | undefined): number => {
  if (!deletedAt) return TRASH_RETENTION_DAYS
  const elapsed = Date.now() - new Date(deletedAt).getTime()
  const remaining = TRASH_RETENTION_DAYS - Math.floor(elapsed / DAY_MS)

  return remaining > 0 ? remaining : 0
}

export const VaultTrashPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const { state, restore, permanentDelete } = useVaultTrash()
  const { folders } = useFolders()

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const onRestore = async (item: VaultItem): Promise<void> => {
    setBusyId(item.id)

    const ok = await restore(item.id)

    setBusyId(null)
    showToast({
      titleKey: ok ? 'vault.trash.restored_toast' : 'vault.trash.restore_failed_toast',
      tone: ok ? 'success' : 'danger',
    })
  }

  const onConfirmDelete = async (): Promise<void> => {
    if (deleteTarget === null) return
    setBusyId(deleteTarget.id)

    const ok = await permanentDelete(deleteTarget.id)

    setBusyId(null)
    setDeleteTarget(null)
    showToast({
      titleKey: ok ? 'vault.trash.deleted_toast' : 'vault.trash.delete_failed_toast',
      tone: ok ? 'success' : 'danger',
    })
  }

  const totalCount = state.kind === 'ready' ? state.items.length : 0
  const failureCount = state.kind === 'ready' ? state.failureCount : 0

  return (
    <div>
      <PageHead
        title={t('vault.trash.title')}
        sub={t('vault.trash.subtitle', { count: totalCount })}
        actions={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              window.location.hash = 'vault'
            }}
          >
            <Icon name="arrow-left" aria-hidden="true" />
            {t('vault.trash.back_button')}
          </button>
        }
      />
      <div className="page-body flush">
        <div className="vault-state-message">
          <Notice tone="info" ico="info">
            {t('vault.trash.notice')}
          </Notice>
        </div>

        {failureCount > 0 && (
          <div className="vault-decryption-warning">
            <Notice tone="warn" ico="alert">
              {t('vault.trash.decryption_warning', { count: failureCount })}
            </Notice>
          </div>
        )}

        {state.kind === 'loading' && (
          <div className="vault-state-message">{t('vault.error.generic')}</div>
        )}

        {state.kind === 'error' && (
          <div className="vault-state-message">
            <Notice tone="danger" ico="alert">
              {t(state.messageKey)}
            </Notice>
          </div>
        )}

        {state.kind === 'ready' && state.items.length === 0 && (
          <EmptyState
            icon="trash"
            title={t('vault.trash.empty_title')}
            sub={t('vault.trash.empty_subtitle')}
          />
        )}

        {state.kind === 'ready' && state.items.length > 0 && (
          <ul className="list vault-list trash-list" role="list">
            {state.items.map(item => {
              const days = daysUntilPurge(item.deletedAt)

              const folderName =
                item.folderId !== null
                  ? (folders.find(f => f.id === item.folderId)?.name ?? null)
                  : null

              return (
                <li
                  key={item.id}
                  className="list-row vault-list-row"
                  aria-label={item.payload.name}
                >
                  <VaultItemAvatar name={item.payload.name} className="vault-row-icon" />
                  <div className="vault-row-main">
                    <div className="nm">{item.payload.name}</div>
                    <div className="sb">
                      {days > 0
                        ? t('vault.trash.expires_soon', { days })
                        : t('vault.trash.expires_today')}
                    </div>
                  </div>
                  <div className="vault-row-tag">
                    <span className="tag">{t(`vault.type.${item.type}`)}</span>
                    {folderName !== null && (
                      <span className="tag vault-row-folder-tag">
                        <Icon name="folder" aria-hidden="true" />
                        {folderName}
                      </span>
                    )}
                  </div>
                  <div className="vault-row-updated sb mono">
                    {t('vault.trash.deleted_prefix')} {formatDateJst(item.deletedAt ?? '')}
                  </div>
                  <div className="acts vault-row-acts trash-row-acts">
                    <button
                      type="button"
                      className="icon-btn sm"
                      title={t('vault.trash.restore')}
                      aria-label={t('vault.trash.restore')}
                      onClick={() => void onRestore(item)}
                      disabled={busyId === item.id}
                      data-testid={`trash-restore-${item.id}`}
                    >
                      <Icon name="refresh" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn sm"
                      title={t('vault.trash.delete_permanently')}
                      aria-label={t('vault.trash.delete_permanently')}
                      onClick={() => setDeleteTarget({ id: item.id, name: item.payload.name })}
                      disabled={busyId === item.id}
                      data-testid={`trash-delete-${item.id}`}
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {deleteTarget !== null && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="trash-delete-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="trash-delete-title">{t('vault.trash.confirm_title')}</h3>
            <p>{t('vault.trash.confirm_body', { name: deleteTarget.name })}</p>
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={busyId === deleteTarget.id}
              >
                {t('vault.trash.confirm_cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void onConfirmDelete()}
                disabled={busyId === deleteTarget.id}
                data-testid="trash-delete-confirm"
              >
                <Icon name="trash" aria-hidden="true" />
                {t('vault.trash.confirm_submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
